/**
 * The Games page's server data (ADR-035), from the public catalog API.
 * Readable signed out; a minute's staleness matches the API's own caching.
 *
 * A cold load that arrived as a snapshot starts each query from the data the
 * snapshot was drawn from (`games-snapshot-data.ts`, ADR-036): the first paint
 * is the content, and the query refreshes it in the background.
 */
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { GameGenrePageData, GameSort } from '@fias/public-site-ui';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  GAMES_SNAPSHOT_DATA_UPDATED_AT,
  takeGameArcheSnapshot,
  takeGameDetailSnapshot,
  takeGameGenreSnapshot,
  takeGamesHomeSnapshot,
  takeGamesMostPlayedSnapshot,
} from '@/lib/games-snapshot-data';

const STALE_MS = 60_000;

/** A search runs from this many characters. */
export const GAMES_SEARCH_MIN_LENGTH = 2;

/** Not-found answers are final; anything else is worth one more try. */
function retryUnlessNotFound(failures: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status === 404) return false;
  return failures < 1;
}

export function useGamesHome() {
  return useQuery({
    queryKey: queryKeys.games.home(),
    queryFn: () => api.getGamesHome(),
    staleTime: STALE_MS,
    initialData: takeGamesHomeSnapshot,
    initialDataUpdatedAt: GAMES_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/** Where the next page of a genre starts, or undefined when every game is loaded. */
function nextGenreOffset(last: GameGenrePageData, pages: GameGenrePageData[]): number | undefined {
  const loaded = pages.reduce((n, page) => n + page.games.length, 0);
  return last.games.length > 0 && loaded < last.total ? loaded : undefined;
}

/** One genre's games, a page (the API's page size) at a time. */
export function useGameGenre(genreId: string, sort: GameSort) {
  return useInfiniteQuery({
    queryKey: queryKeys.games.genre(genreId, sort),
    queryFn: ({ pageParam }) => api.getGameGenre(genreId, sort, pageParam),
    initialPageParam: 0,
    getNextPageParam: nextGenreOffset,
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    // Changing the sort keeps the list on screen until the new order arrives.
    placeholderData: keepPreviousData,
    // A snapshot lists a genre most popular first.
    initialData: sort === 'popular' ? () => takeGameGenreSnapshot(genreId) : undefined,
    initialDataUpdatedAt: GAMES_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/**
 * The loaded pages of a genre as one page: every game once (a game can move
 * between pages while they load), the newest total.
 */
export function flattenGenrePages(pages: GameGenrePageData[]): GameGenrePageData | null {
  if (pages.length === 0) return null;
  const seen = new Set<string>();
  const games = pages
    .flatMap((page) => page.games)
    .filter((game) => {
      if (seen.has(game.slug)) return false;
      seen.add(game.slug);
      return true;
    });
  const last = pages[pages.length - 1];
  return { genre: last.genre, subgenres: last.subgenres, games, total: last.total };
}

export function useGameDetail(slug: string) {
  return useQuery({
    queryKey: queryKeys.games.detail(slug),
    queryFn: () => api.getGame(slug),
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    initialData: () => takeGameDetailSnapshot(slug),
    initialDataUpdatedAt: GAMES_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/** Every game in one arche: `/a/games/from/<archeKey>`. */
export function useGameArche(archeKey: string) {
  return useQuery({
    queryKey: queryKeys.games.arche(archeKey),
    queryFn: () => api.getGameArche(archeKey),
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    initialData: () => takeGameArcheSnapshot(archeKey),
    initialDataUpdatedAt: GAMES_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/** The most played games, ranked: `/a/games/most-played`. */
export function useGamesMostPlayed() {
  return useQuery({
    queryKey: queryKeys.games.mostPlayed(),
    queryFn: () => api.getGamesMostPlayed(),
    staleTime: STALE_MS,
    initialData: takeGamesMostPlayedSnapshot,
    initialDataUpdatedAt: GAMES_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/**
 * Games search for `query` (already debounced by the caller). Off below
 * {@link GAMES_SEARCH_MIN_LENGTH} characters. Results stay up while the next
 * query's arrive.
 */
export function useGamesSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.games.search(q),
    queryFn: ({ signal }) => api.searchGames(q, { signal }),
    enabled: q.length >= GAMES_SEARCH_MIN_LENGTH,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
    retry: false,
  });
}
